<script setup lang="ts">
import { useHome } from '@/composable/useHome'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CreditCard, MessageCircleWarning } from 'lucide-vue-next'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { usePersistentStorage } from '@/composable/usePersistentStorage'
import { toast } from 'vue-sonner'
import Button from '@/components/ui/button/Button.vue'

const {
  isLoading,
  shownKanjisCount,
  totalKanjisCount,
  shownWordsCount,
  totalWordsCount,
  flashcardsForReviewCount,
} = useHome()

const { isPersistent, error, tryPersistingStorage } = usePersistentStorage()

const persistStorage = async () => {
  if (await tryPersistingStorage()) {
    toast('Storage persisted successfully!')
    return
  } else {
    toast('Failed to persist storage...')
  }
}
</script>

<template>
  <main>
    <h1 class="mb-4">Home</h1>
    <div v-if="isLoading" class="p-10 text-center">
      <p>Loading your data...</p>
    </div>
    <div v-else class="flex flex-wrap">
      <Alert v-if="!isPersistent" class="mx-2">
        <MessageCircleWarning class="w-4 h-4" />
        <AlertTitle>Note about storing your data</AlertTitle>
        <AlertDescription>
          <p>
            The data is stored locally in the browser on your device. There is a possibility that
            when cleaning device or browser data, or simply due to memory constraints the data will
            be wiped. You can persist the storage if you want to avoid that.
          </p>
          <p>
            If for any reason persisting is not an option, make sure to backup your data frequently
            from the <RouterLink to="/settings" class="inline underline">Settings</RouterLink> page.
          </p>
          <p>
            <Button @click="persistStorage" class="mt-2 cursor-pointer">Persist storage</Button>
            {{ error }}
          </p>
        </AlertDescription>
      </Alert>
      <div class="p-2 w-full sm:w-1/3">
        <Card
          class="py-4 gap-4"
          :class="[flashcardsForReviewCount > 0 ? 'border-yellow-200' : 'border-green-200']"
        >
          <CardHeader class="flex flex-row items-center justify-between space-y-0">
            <CardTitle class="!pb-0 text-sm font-medium">Flashcards left</CardTitle>
            <CreditCard class="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div class="text-2xl font-bold">{{ flashcardsForReviewCount }}</div>
          </CardContent>
        </Card>
      </div>
      <div class="p-2 w-full sm:w-1/3">
        <Card class="py-4 gap-4">
          <CardHeader class="flex flex-row items-center justify-between space-y-0">
            <CardTitle class="!pb-0 text-sm font-medium"> Shown Kanjis </CardTitle>
            <span class="h-4 w-4 text-muted-foreground"> 猫 </span>
          </CardHeader>
          <CardContent>
            <div class="text-2xl font-bold">
              {{ shownKanjisCount }}<small class="opacity-50"> / {{ totalKanjisCount }}</small>
            </div>
          </CardContent>
        </Card>
      </div>
      <div class="p-2 w-full sm:w-1/3">
        <Card class="py-4 gap-4">
          <CardHeader class="flex flex-row items-center justify-between space-y-0">
            <CardTitle class="!pb-0 text-sm font-medium">Shown Words</CardTitle>
            <span class="h-4 text-muted-foreground"> 子猫 </span>
          </CardHeader>
          <CardContent>
            <div class="text-2xl font-bold">
              {{ shownWordsCount }}<small class="opacity-50"> / {{ totalWordsCount }}</small>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  </main>
</template>
